import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ShieldAlert, Info, CheckCircle2 } from "lucide-react";

type AccessUpdate = {
  type: "removed" | "granted";
  businessName: string;
  outletName: string;
  hasOtherOutlets?: boolean;
};

export const StaffAccessMonitor = () => {
  const { user, isStaff, signOut } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [accessUpdate, setAccessUpdate] = useState<AccessUpdate | null>(null);

  useEffect(() => {
    // Only monitor if the user is logged in and acting as staff
    if (!user || !isStaff || !user.email) return;

    const channel = supabase
      .channel("staff_access_monitor")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "staff_members",
          // Matched on user_id, not email: that's the column RLS scopes staff
          // reads by, so the subscription and the policy agree on visibility.
          filter: `user_id=eq.${user.id}`,
        },
        async (payload) => {
          let isRevoked = false;
          let isGranted = false;
          let recordRef = null;

          if (payload.eventType === "DELETE") {
            isRevoked = true;
            recordRef = payload.old;
          } else if (payload.eventType === "INSERT") {
            const newRecord = payload.new as any;
            if (newRecord.status === "pending" || newRecord.status === "active") {
              isGranted = true;
              recordRef = newRecord;
            }
          } else if (payload.eventType === "UPDATE") {
            const newRecord = payload.new as any;
            const oldRecord = payload.old as any;
            if (newRecord.status === "deactivated" && oldRecord.status !== "deactivated") {
              isRevoked = true;
              recordRef = newRecord;
            } else if ((newRecord.status === "active" || newRecord.status === "pending") && oldRecord.status === "deactivated") {
              isGranted = true;
              recordRef = newRecord;
            }
          }

          if ((isRevoked || isGranted) && recordRef) {
            try {
              // 1. Fetch names for the modal
              const [businessRes, outletRes] = await Promise.all([
                supabase.from("businesses").select("business_name").eq("business_id", recordRef.business_id).maybeSingle(),
                supabase.from("outlets").select("outlet_name").eq("outlet_id", recordRef.outlet_id).maybeSingle(),
              ]);

              const businessName = businessRes.data?.business_name || "a business";
              const outletName = outletRes.data?.outlet_name || "an outlet";

              if (isRevoked) {
                // 2. Check if the user has other active/pending outlets
                const { count } = await supabase
                  .from("staff_members")
                  .select("*", { count: "exact", head: true })
                  .eq("user_id", user.id)
                  .in("status", ["active", "pending"]);

                const hasOtherOutlets = (count || 0) > 0;
                setAccessUpdate({ type: "removed", businessName, outletName, hasOtherOutlets });
              } else if (isGranted) {
                // Grants for an already-linked staff member arrive already
                // active (invite-staff fills user_id server-side), so there is
                // nothing to claim here. Just tell them.
                setAccessUpdate({ type: "granted", businessName, outletName });
              }

              // 3. Force the AuthContext to re-evaluate state
              queryClient.invalidateQueries({ queryKey: ["auth-session"] });
              queryClient.invalidateQueries({ queryKey: ["staffOutlets"] });

            } catch (err) {
              console.error("Error handling staff access update:", err);
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, isStaff, queryClient]);

  const handleAction = async () => {
    if (accessUpdate?.type === "granted") {
      setAccessUpdate(null);
    } else if (accessUpdate?.type === "removed") {
      if (accessUpdate.hasOtherOutlets) {
        setAccessUpdate(null);
      } else {
        await signOut();
        navigate("/auth");
      }
    }
  };

  if (!accessUpdate) return null;

  const isGranted = accessUpdate.type === "granted";
  const isPartialRevoke = accessUpdate.type === "removed" && accessUpdate.hasOtherOutlets;
  const isFullRevoke = accessUpdate.type === "removed" && !accessUpdate.hasOtherOutlets;

  return (
    <AlertDialog open={!!accessUpdate}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4 ${
            isGranted ? 'bg-green-100' : isPartialRevoke ? 'bg-blue-100' : 'bg-red-100'
          }`}>
            {isGranted ? (
              <CheckCircle2 className="w-6 h-6 text-green-600" />
            ) : isPartialRevoke ? (
              <Info className="w-6 h-6 text-blue-600" />
            ) : (
              <ShieldAlert className="w-6 h-6 text-red-600" />
            )}
          </div>
          <AlertDialogTitle className="text-center text-xl">
            {isGranted ? "New Access Granted" : isPartialRevoke ? "Outlet Access Removed" : "Access Removed"}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-center text-base">
            {isGranted ? (
              <>You have been granted access to <span className="font-semibold text-foreground">{accessUpdate.businessName}'s {accessUpdate.outletName}</span>.</>
            ) : (
              <>Your access to <span className="font-semibold text-foreground">{accessUpdate.businessName}'s {accessUpdate.outletName}</span> has been removed by the administrator.</>
            )}
            <br /><br />
            {isGranted 
              ? "You can now select this outlet from the dropdown menu."
              : isPartialRevoke 
                ? "You still have access to your other assigned outlets." 
                : "You will be signed out of this session."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="sm:justify-center">
          <AlertDialogAction onClick={handleAction} className="w-full sm:w-auto">
            {isGranted ? "Got it" : isPartialRevoke ? "Got it" : "Sign out and return to Auth"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
